import React, {
  RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  FormProvider,
  useForm,
} from 'react-hook-form';
import Dialog from 'pages/Dashboard/components/Dialog';
import noop from 'lodash/noop';
import {
  EncounterWithPatientPaymentInfoViewModel,
  PatientDTO,
} from 'dtos';
import PanelExtension from 'core/components/PanelExtension';
import {
  clsx,
} from 'clsx';
import IconButton from 'core/components/IconButton';
import CollapseIcon from 'core/icons/collapse';
import AdjustmentForm, {
  AdjustmentFormData,
} from 'views/EMR/Billing/AdjustPayment/AdjustmentForm';
import MoveToCreditLogs from 'views/EMR/Billing/AdjustPayment/MoveToCreditLogs';
import RefundLogs from 'views/EMR/Billing/AdjustPayment/RefundLogs';
import isNil from 'lodash/isNil';

type AdjustPaymentView = {
  type: 'refund' | 'move-to-credit';
  hideNavigation?: boolean;
  defaultTitle?: string;
};

export type AdjustPaymentDialogParams = {
  patient?: PatientDTO;
  amount?: number;
  view?: AdjustPaymentView;
};

const defaultView: AdjustPaymentView = {
  type: 'move-to-credit',
  hideNavigation: false,
  defaultTitle: 'Adjust Payments',
};

type Props = AdjustPaymentDialogParams & {
  onClose: () => void;
};

export type EncounterWithPayment = EncounterWithPatientPaymentInfoViewModel & {
  moveToCreditAmount?: number;
  suggestedMoveToCreditAmount?: number;
  suggestedAmount?: number;
  refundAmount?: number;
  isCredit?: boolean;
};

export default function MoveToCreditDialog({
  patient,
  onClose,
  view = defaultView,
}: Props) {
  const form = useForm<AdjustmentFormData>({
    mode: 'all',
    defaultValues: {
      patient: patient ?? undefined,
      comment: '',
    },
  });

  const { watch } = form;

  const selectedPatient = watch('patient');

  const {
    type: viewType,
    hideNavigation,
    defaultTitle,
  } = view;

  const dialogRef = useRef<HTMLDivElement>(null) as RefObject<HTMLDivElement>;
  const moveToCreditFormRef = useRef<HTMLDivElement>(null) as RefObject<HTMLDivElement>;
  const [expanded, setExpanded] = useState<boolean>(false);

  const handleExpand = () => {
    setExpanded(!expanded);
  };

  const [maxHeight, setMaxHeight] = useState<number | null>(null);

  useEffect(() => {
    if (moveToCreditFormRef.current) {
      const height = moveToCreditFormRef.current.offsetHeight;
      setMaxHeight(height);
    }
  }, [expanded]);

  const [
    adjustmentType,
    setAdjustmentType,
  ] = useState<number>(Number(viewType === 'refund'));

  const isMoveToCredit = adjustmentType === 0;
  const shouldLockPatient = !isNil(patient);

  return (
    <Dialog
      open
      onClose={onClose}
      onConfirm={noop}
      confirmText="Confirm"
      className={clsx('overflow-y-hidden populate-form min-h-[320px] transition-[width] !p-0 !max-h-[calc(100vh-5rem)]', {
        '!w-176': !expanded,
        '!w-[1400px]': expanded,
      })}
      showActions={false}
      hideCloseBtn
      dialogRef={dialogRef}
      disableEscapeKeyDown
      disableBackdropClick
    >
      <div className="flex">
        <FormProvider {...form}>
          <AdjustmentForm
            onClose={onClose}
            onExpand={handleExpand}
            dialogRef={dialogRef}
            moveToCreditFormRef={moveToCreditFormRef}
            adjustmentType={adjustmentType}
            setAdjustmentType={setAdjustmentType}
            hideNavigation={hideNavigation}
            shouldLockPatient={shouldLockPatient}
            defaultTitle={defaultTitle}
          />
        </FormProvider>
        <PanelExtension className={clsx('transition-[width] rounded-r-lg', {
          'w-0 !h-0': !expanded,
          'w-[640px]': expanded,
        })}
        >
          <div className="w-full bg-gray-50">
            <div className="w-full flex gap-4 p-4">
              <IconButton
                onClick={() => { setExpanded(false); }}
                tooltip="Hide"
              >
                <CollapseIcon className="!w-4 !h-4 !text-inherit rotate-180" />
              </IconButton>
              <h3 className="font-semibold text-lg">
                {isMoveToCredit
                  ? 'Move to Credit History'
                  : 'Refund History'}
              </h3>
            </div>
            {isMoveToCredit ? (
              <MoveToCreditLogs
                patientId={patient?.patientId ?? 0}
                maxHeight={maxHeight}
              />
            ) : (
              <RefundLogs
                patientId={selectedPatient?.patientId ?? 0}
                maxHeight={maxHeight}
              />
            )}
          </div>
        </PanelExtension>
      </div>
    </Dialog>
  );
}
